import { CalendarIcon } from '@phosphor-icons/react';
import { format, startOfMonth, startOfYear, subDays } from 'date-fns';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export interface DatePickerProps {
  mode?: 'single' | 'range' | 'compact';
  value?: Date | DateRange | string;
  onChange?: (value: any) => void;
  placeholder?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  presets?: boolean;
  /**
   * Dates to dot with a subtle indicator (single mode) — e.g. days that already
   * have a scheduled post, so the user can avoid stacking everything on one day.
   * Opt-in: omit it and there's zero added cost (no modifier, no extra render).
   */
  markedDates?: Date[];
  ariaLabel?: string;
  /** Lets a `<Label htmlFor>` associate with the trigger button, same as any other field. */
  id?: string;
}

const PREDEFINED_RANGES = {
  'Last 7 Days': { from: subDays(new Date(), 6), to: new Date() },
  'Last 30 Days': { from: subDays(new Date(), 29), to: new Date() },
  'This Month': { from: startOfMonth(new Date()), to: new Date() },
  'Last Month': {
    from: startOfMonth(subDays(startOfMonth(new Date()), 1)),
    to: subDays(startOfMonth(new Date()), 1),
  },
  'Last 3 Months': { from: subDays(new Date(), 90), to: new Date() },
  'This Year': { from: startOfYear(new Date()), to: new Date() },
};

export const DatePicker: React.FC<DatePickerProps> = ({
  mode = 'single',
  value,
  onChange,
  placeholder = 'Pick a date',
  className,
  minDate,
  maxDate,
  presets = false,
  markedDates,
  ariaLabel,
  id,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  if (mode === 'compact') {
    const externalValue = typeof value === 'string' ? value : undefined;
    const selectProps = externalValue
      ? { value: externalValue, onValueChange: onChange }
      : { defaultValue: '30d', onValueChange: onChange };
    return (
      <Select {...selectProps}>
        <SelectTrigger
          className={cn(
            'flex items-center h-8 pl-3 pr-2.5 rounded-full border border-border text-xs font-medium text-foreground/80 hover:bg-muted/20 hover:text-foreground hover:border-border transition-colors focus:ring-0 focus:outline-none w-auto shadow-sm',
            className,
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7d">7D</SelectItem>
          <SelectItem value="30d">30D</SelectItem>
          <SelectItem value="90d">All</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  const handleRangeChange = (range: string): void => {
    const selectedRange = PREDEFINED_RANGES[range as keyof typeof PREDEFINED_RANGES];
    if (selectedRange && onChange) {
      onChange(selectedRange);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          aria-label={ariaLabel}
          variant="outline"
          size={mode === 'range' ? 'sm' : 'default'}
          className={cn(
            // `rounded-[var(--radius)]`, not the size variant's `rounded-full`:
            // every real caller (CalendarMomentDialog, CreateReachEventDialog,
            // GoalDialog) places this beside an Input/Select field, both of
            // which use this same radius — the pill shape is correct for
            // `mode="compact"` (a standalone filter control, still rounded-full
            // above) but reads as a mismatched control here. Founder,
            // 2026-09-16: "why is the date roundness on that field different
            // than the other component here on the page."
            'w-full justify-start rounded-[var(--radius)] text-left font-normal',
            mode !== 'range' && 'h-10',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon data-icon="inline-start" weight="regular" />
          {mode === 'single' ? (
            (value as Date) ? (
              // Short form, matching range mode two lines below and every
              // other date display in the app (`'MMM d, yyyy'`/`'LLL dd, y'`
              // — both render e.g. "Sep 10, 2026"). `'PPP'` ("September
              // 10th, 2026") was the sole long-form outlier and overflowed
              // narrow callers like `CalendarMomentDialog`'s Starts/Ends
              // fields. Founder, 2026-09-09: "there shouldn't be long dates
              // there... it should be a shortened date."
              format(value as Date, 'LLL dd, y')
            ) : (
              <span>{placeholder}</span>
            )
          ) : (value as DateRange)?.from ? (
            (value as DateRange).to ? (
              <>
                {format((value as DateRange).from!, 'LLL dd, y')} -{' '}
                {format((value as DateRange).to!, 'LLL dd, y')}
              </>
            ) : (
              format((value as DateRange).from!, 'LLL dd, y')
            )
          ) : (
            <span>Pick a date range</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-popover" align="start" sideOffset={4}>
        {mode === 'range' && presets && (
          <div className="flex flex-col gap-2 p-3 pb-0">
            <Select onValueChange={handleRangeChange}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select a range" />
              </SelectTrigger>
              <SelectContent position="popper" className="dark:border-border/60 dark:bg-card">
                {Object.keys(PREDEFINED_RANGES).map((range) => (
                  <SelectItem key={range} value={range}>
                    {range}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="border-t border-border dark:border-border/60 pt-3 mt-1" />
          </div>
        )}
        {/* No padding for the simple date mode — <Calendar> already applies its
            own p-3, so a wrapper p-3 here double-padded the popover. Range+presets
            keeps px-3 pb-3 to align the two-month grid under the preset select. */}
        <div className={cn(mode === 'range' && presets ? 'px-3 pb-3' : undefined)}>
          <Calendar
            initialFocus
            mode={mode as any}
            defaultMonth={mode === 'range' ? (value as DateRange)?.from : (value as Date)}
            selected={value as any}
            onSelect={(newVal) => {
              if (onChange)
                onChange(
                  newVal || (mode === 'range' ? { from: undefined, to: undefined } : undefined),
                );
              if (mode === 'single' && newVal) setIsOpen(false);
              if (mode === 'range' && (newVal as DateRange)?.from && (newVal as DateRange)?.to)
                setIsOpen(false);
            }}
            numberOfMonths={mode === 'range' ? 2 : 1}
            modifiers={markedDates && markedDates.length ? { marked: markedDates } : undefined}
            disabled={(date) => {
              if (minDate && date < minDate) return true;
              if (maxDate && date > maxDate) return true;
              return false;
            }}
            className="dark:bg-card dark:text-card-foreground"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
